<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="primary">
                <ion-buttons slot="start">
                    <ion-back-button v-if="loaded" :text="$t('report_sheets.top_bar.back_to_steps')" :default-href="`/report-sheets/${sheet?.uuid}`"></ion-back-button>
                </ion-buttons>
                <ion-title>{{ $t('report_sheets.top_bar.title') }} : {{ sheet?.title ?? $t('report_sheets.top_bar.loading') }} {{ sheet?.created_at ? ` - ${formatDate(sheet.created_at)}` : '' }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content>
            <div v-if="!loaded">
                <EquipmentSkeleton/>
            </div>
            <div v-else>
                <div :class="$style['print-header']">
                    <h1>{{ $t('report_sheets.top_bar.title') }} : {{ sheet?.title }} - {{ formatDate(sheet?.created_at) }}</h1>
                </div>
                <div :class="$style['container-table']">
                    <ion-grid :classs="$style['title']">
                        <ion-row :class="$style['title']">{{ $t('report_sheets.page_resume.data_table.employees.title') }} ({{ sheet?.employees.length ?? 0 }})</ion-row>
                    </ion-grid>

                    <div :class="$style['table-wrapper']">
                        <table :class="$style['data-table']">
                            <thead>
                                <tr>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_employee_number') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_name') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_activity') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_comment') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_travel') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_mobility') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_start_at') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_end_at') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_total_rest') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_total') }}</th>
                                    <th v-if="user.rights?.includes('report_sheets.manager')">Coût (€)</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_mobility_code') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.employees.col_other_service') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="employee in sheet?.employees" :key="employee.uuid">
                                    <td>{{ employee.reference }}</td>
                                    <td>{{ employee.firstname }} {{ employee.lastname }}</td>
                                    <td>{{ employee.activity }}</td>
                                    <td>{{ employee.comment }}</td>
                                    <td>{{ employee.travel }}</td>
                                    <td>{{ employee.mobility }}</td>
                                    <td>{{ employee.start_at }}</td>
                                    <td>{{ employee.end_at }}</td>
                                    <td>{{ employee.rest_time }}</td>
                                    <td>{{ employee.total_hours }}</td>
                                    <td v-if="user.rights?.includes('report_sheets.manager')">{{ employee.total_cost }}</td>
                                    <td :class="$style['truncate']">{{ employee.mobility_code }}</td>
                                    <td>{{ employee.services_other }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <ion-grid v-if="sheet?.total_employees_cost">
                        <ion-row :class="$style['total-cost-title']">{{ $t('report_sheets.page_resume.data_table.employees.total_cost_title') }} : {{ sheet?.total_employees_cost }}€</ion-row>
                    </ion-grid>
                </div>

                <div :class="$style['container-table']">
                    <ion-grid>
                        <ion-row :class="$style['title']">{{ $t('report_sheets.page_resume.data_table.materials.title') }} ({{ sheet?.equipments.length ?? 0 }})</ion-row>
                    </ion-grid>

                    <div :class="$style['table-wrapper']">
                        <table :class="$style['data-table']">
                            <thead>
                                <tr>
                                    <th>{{ $t('report_sheets.page_resume.data_table.materials.col_title') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.materials.col_name') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.materials.col_unity') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.materials.col_quantity') }}</th>
                                    <th v-if="user.rights?.includes('report_sheets.manager')">Coût (€)</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.materials.col_employee') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="equipment in sheet?.equipments" :key="equipment.uuid">
                                    <td>{{ equipment.reference }}</td>
                                    <td>{{ equipment.name }}</td>
                                    <td>{{ equipment.unity }}</td>
                                    <td>{{ equipment.quantity }}</td>
                                    <td v-if="user.rights?.includes('report_sheets.manager')">{{ equipment.total_cost }}</td>
                                    <td>{{ equipment.employee_firstname }} {{ equipment.employee_lastname }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <ion-grid v-if="sheet?.total_equipments_cost">
                        <ion-row :class="$style['total-cost-title']">Coût total du matériel : {{ sheet?.total_equipments_cost }}€</ion-row>
                    </ion-grid>
                </div>

                <div :class="$style['container-table']">
                    <div :class="$style['table-wrapper']">
                        <ion-grid>
                            <ion-row :class="$style['title']">{{ $t('report_sheets.page_resume.data_table.locations.title') }} ({{ sheet?.locations.length ?? 0 }})</ion-row>
                        </ion-grid>

                        <table :class="$style['data-table']">
                            <thead>
                                <tr>
                                    <th>{{ $t('report_sheets.page_resume.data_table.locations.col_title') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.locations.col_unity') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.locations.col_quantity') }}</th>
                                    <th v-if="user.rights?.includes('report_sheets.manager')">Coût (€)</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.locations.col_employee') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="location in sheet?.locations" :key="location.uuid">
                                    <td>{{ location.name }}</td>
                                    <td>{{ location.unity }}</td>
                                    <td>{{ location.quantity }}</td>
                                    <td v-if="user.rights?.includes('report_sheets.manager')">{{ location.total_cost }}</td>
                                    <td>{{ location.employee_firstname }} {{ location.employee_lastname }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <ion-grid v-if="sheet?.total_locations_cost ">
                        <ion-row :class="$style['total-cost-title']">Coût total des locations : {{ sheet?.total_locations_cost }}€</ion-row>
                    </ion-grid>
                </div>

                <div :class="$style['container-table']">
                    <div :class="$style['table-wrapper']">
                        <ion-grid>
                            <ion-row :class="$style['title']">{{ $t('report_sheets.page_resume.data_table.outsourced_works.title') }} ({{ sheet?.outsourced_works.length ?? 0 }})</ion-row>
                        </ion-grid>

                        <table :class="$style['data-table']">
                            <thead>
                                <tr>
                                    <th>{{ $t('report_sheets.page_resume.data_table.outsourced_works.col_title') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.outsourced_works.col_unity') }}</th>
                                    <th>{{ $t('report_sheets.page_resume.data_table.outsourced_works.col_quantity') }}</th>
                                    <th v-if="user.rights?.includes('report_sheets.manager')">Coût (€)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="outsourced_work in sheet?.outsourced_works" :key="outsourced_work.uuid">
                                    <td>{{ outsourced_work.name }}</td>
                                    <td>{{ outsourced_work.unity }}</td>
                                    <td>{{ outsourced_work.quantity }}</td>
                                    <td v-if="user.rights?.includes('report_sheets.manager')">{{ outsourced_work.total_cost }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <ion-grid v-if=" sheet?.total_outsourced_work_cost">
                        <ion-row :class="$style['total-cost-title']">Coût total des locations : {{ sheet?.total_outsourced_work_cost }}€</ion-row>
                    </ion-grid>
                </div>
            </div>
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
    import {
        IonBackButton,
        IonButtons,
        IonContent,
        IonHeader,
        IonPage,
        IonToolbar,
        IonGrid,
        IonRow
    } from "@ionic/vue"

    import { defineComponent } from "vue"
    import { ReportSheet } from "@/report-sheets"
    import EquipmentSkeleton from "@/components/skeletons/EquipmentSkeleton.vue"
    import { useUserStore } from "@/stores/user"
    import moment from "moment"

    export default defineComponent({
        components: {
            IonBackButton,
            IonButtons,
            IonContent,
            IonHeader,
            IonPage,
            EquipmentSkeleton,
            IonToolbar,
            IonGrid,
            IonRow
        },
        setup() {
            const user = useUserStore()

            return { 
                user 
            }
        },
        data() {
            return {
                sheet: null as ReportSheet | null,
                loaded: false as boolean | false
            }
        },
        mounted() {
          if (this.user.is_logged_in)
            this.load()

          this.$watch("user.is_logged_in", (is_logged_in: boolean) => {
            if (is_logged_in) this.load();
          });
        },
        methods: {
            load() {
                this.axios.get("/report-sheets/" + this.$route.params.uuidReport)
                .then((response) => {
                    this.sheet = response.data
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            formatDate(date: string) {
                return moment(date).format("DD/MM/YYYY")
            }
        }
    })
</script>

<style lang="scss" module>
    ion-grid {
        padding: 0;
    }
    ion-row.title {
        background: #000;
        color: #fff;
        display: flex;
        justify-content: center;
        padding: 10px 0;
    }
    ion-row.total-cost-title {
        background: var(--ion-color-primary);
        color: #fff;
        display: flex;
        justify-content: end;
        padding: 10px;
    }
    ion-row.header {
        background: #9e9e9e;
    }
    ion-row.data:nth-child(even) {
        background: #ededed;
    }
    .container-table {
        margin: 20px;
    }
    .table-wrapper {
        overflow-x: auto;
        white-space: nowrap;
    }
    .data-table {
        border-collapse: collapse;
        width: 100%;
        white-space: nowrap;
    }
    .data-table td, .data-table th {
        border: 1px solid #ddd;
        padding: 8px;
    }
    // .truncate {
        // text-overflow: ellipsis;
        // overflow: hidden;
        // max-width: 400px;
    // }
    .data-table tr:nth-child(even) {
        background-color: #f2f2f2;
    }
    .data-table tr:hover {
        background-color: #ddd;
    }
    .data-table th {
        padding-top: 12px;
        padding-bottom: 12px;
        text-align: left;
        background-color: #c9c9c9;
    }
    
    .print-header {
        display: none;
    }
    
    @media print {
        .print-header {
            display: block;
            text-align: center;
            margin-bottom: 20px;
        }
        
        .print-header h1 {
            font-size: 20px;
            margin: 0;
            padding: 10px 0;
        }
        
        ion-header {
            display: none !important;
        }
        
        ion-tab-bar {
            display: none !important;
        }
        
        ion-menu {
            display: none !important;
        }
        
        ion-content {
            --padding-top: 0 !important;
            --padding-bottom: 0 !important;
        }
        
        .container-table {
            margin: 10px;
            page-break-inside: avoid;
        }
        
        .table-wrapper {
            overflow-x: visible !important;
        }
        
        .data-table {
            font-size: 10px;
        }
        
        .data-table td, .data-table th {
            padding: 4px;
        }
        
        ion-row.title {
            background: #000 !important;
            color: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        
        ion-row.total-cost-title {
            background: var(--ion-color-primary) !important;
            color: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        
        .data-table tr:nth-child(even) {
            background-color: #f2f2f2 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        
        .data-table th {
            background-color: #c9c9c9 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
    }
</style>